'use client';

import type { SalesforceCreditPreview } from '@/@admin/types/entities';
import { portalApi } from '@/lib/http/axios';
import { getSession } from 'next-auth/react';

import type {
    IClientCreditConsumptionSummary,
    ICreditRateCurrent,
    ICreditRateVersion,
    IGlobalCreditSettings,
    IOrgCreditSettings,
    IMarginReport,
    ICreditCostCurrent,
    ICreditCostVersion,
    IPublishCreditCostPayload,
    IPublishCreditRatePayload,
    IOrgFleetSummary,
    IOrgSearchResult,
    IPaginatedTransactions,
    ISyncSalesforceResult,
    IUpdateOrgSettingsPayload,
    IUserDaniUsage,
} from './types/entities';
import type {
    AddCreditsPayload,
    AdjustmentHistoryRow,
    AdjustmentPermissions,
    AdjustmentReason,
    AdjustmentResult,
    ExtendGrantPayload,
    RemoveCreditsPayload,
    ReverseRowPayload,
    TransferCreditsPayload,
} from './types/adjustments';
import type { CreditBalance } from './types/balance';
import type { ICogsInvoiceInput, ICogsResponse } from './types/cogs';
import type {
    IAddLedgerShadowNotePayload,
    ILedgerShadowNote,
    ILedgerShadowReport,
} from './types/shadow';
import type { SpendCheckRequest, SpendCheckResult } from './types/spend-check';
import { API_ENDPOINTS } from './types/constants';
import type { IUserSpendLimitRow } from './utils/user-spend-limits';

export interface IPeriod {
    month?: number;
    year?: number;
}

class CreditConsumptionService {
    async getSummary(period?: IPeriod): Promise<IClientCreditConsumptionSummary> {
        const { data } = await portalApi.get<IClientCreditConsumptionSummary>(
            API_ENDPOINTS.SUMMARY,
            {
                params:
                    period?.month || period?.year
                        ? { month: period?.month, year: period?.year }
                        : undefined,
            },
        );
        return data;
    }

    async getTransactions(
        page = 1,
        limit = 50,
        includeZeroCredit = false,
    ): Promise<IPaginatedTransactions> {
        const { data } = await portalApi.get<IPaginatedTransactions>(API_ENDPOINTS.TRANSACTIONS, {
            params: { page, limit, ...(includeZeroCredit ? { includeZeroCredit: true } : {}) },
        });
        return data;
    }

    /**
     * The pre-check (M4S-5912, §6): can the org afford this before it starts? Advisory; the spend
     * is decided again when it runs. Writes nothing, so View-as allows it (`credit_spend_check`).
     * Under View-as pass the client's `masqueradeOrgId`, as DANI does, or the staff member's own
     * org is read. Show a refusal with `spendCheckCopy`.
     */
    async spendCheck(request: SpendCheckRequest): Promise<SpendCheckResult> {
        const { data } = await portalApi.post<SpendCheckResult>(API_ENDPOINTS.SPEND_CHECK, {
            purpose: request.purpose,
            ...(request.credits !== undefined ? { credits: request.credits } : {}),
            ...(request.masqueradeOrgId ? { masqueradeOrgId: request.masqueradeOrgId } : {}),
        });
        return data;
    }

    async getUsers(period?: IPeriod): Promise<IUserDaniUsage[]> {
        const { data } = await portalApi.get<IUserDaniUsage[]>(API_ENDPOINTS.USERS, {
            params:
                period?.month || period?.year
                    ? { month: period?.month, year: period?.year }
                    : undefined,
        });
        return data;
    }

    async getFleet(params?: {
        planTier?: string;
        orgName?: string;
        month?: number;
        year?: number;
    }): Promise<IOrgFleetSummary[]> {
        const { data } = await portalApi.get<IOrgFleetSummary[]>(API_ENDPOINTS.FLEET, { params });
        return data;
    }

    async getOrgDetail(orgId: string, period?: IPeriod): Promise<IOrgFleetSummary> {
        const { data } = await portalApi.get<IOrgFleetSummary>(API_ENDPOINTS.ORG_DETAIL(orgId), {
            params:
                period?.month || period?.year
                    ? { month: period?.month, year: period?.year }
                    : undefined,
        });
        return data;
    }

    async getOrgUsers(orgId: string, period?: IPeriod): Promise<IUserDaniUsage[]> {
        const { data } = await portalApi.get<IUserDaniUsage[]>(API_ENDPOINTS.ORG_USERS(orgId), {
            params:
                period?.month || period?.year
                    ? { month: period?.month, year: period?.year }
                    : undefined,
        });
        return data;
    }

    async getOrgTransactions(
        orgId: string,
        page = 1,
        limit = 50,
        includeGrants = false,
        includeZeroCredit = false,
    ): Promise<IPaginatedTransactions> {
        const { data } = await portalApi.get<IPaginatedTransactions>(
            API_ENDPOINTS.ORG_TRANSACTIONS(orgId),
            {
                params: {
                    page,
                    limit,
                    ...(includeGrants ? { includeGrants: true } : {}),
                    ...(includeZeroCredit ? { includeZeroCredit: true } : {}),
                },
            },
        );
        return data;
    }

    async postOverride(
        orgId: string,
        payload: { overrideCredits: number; expiryDate?: string | null; note: string },
    ): Promise<{ success: boolean; overrideCredits: number; expiryDate: string | null }> {
        const { data } = await portalApi.post(API_ENDPOINTS.OVERRIDE(orgId), payload);
        return data;
    }

    async revokeOverride(orgId: string, overrideId: string): Promise<void> {
        await portalApi.delete(API_ENDPOINTS.REVOKE_OVERRIDE(orgId, overrideId));
    }

    async revokeTopoff(orgId: string, topoffId: string): Promise<void> {
        await portalApi.delete(API_ENDPOINTS.REVOKE_TOPOFF(orgId, topoffId));
    }

    async updateOrgSettings(
        orgId: string,
        payload: IUpdateOrgSettingsPayload,
    ): Promise<IOrgCreditSettings> {
        const { data } = await portalApi.patch<IOrgCreditSettings>(
            API_ENDPOINTS.ORG_SETTINGS(orgId),
            payload,
        );
        return data;
    }

    async listUserSpendLimits(orgId: string): Promise<IUserSpendLimitRow[]> {
        const { data } = await portalApi.get(API_ENDPOINTS.USER_SPEND_LIMITS(orgId));
        return data;
    }

    async setUserSpendLimit(
        orgId: string,
        orgUserId: number,
        monthlyCreditLimit: number | null,
    ): Promise<void> {
        await portalApi.patch(API_ENDPOINTS.USER_SPEND_LIMITS(orgId), {
            orgUserId,
            monthlyCreditLimit,
        });
    }

    async setDaniCap(orgId: string, monthlyCap: number, note?: string): Promise<void> {
        await portalApi.patch(API_ENDPOINTS.DANI_CAP(orgId), {
            monthlyCap,
            ...(note ? { note } : {}),
        });
    }

    async setContractPool(
        orgId: string,
        payload: {
            totalContractedCredits: number;
            contractStartDate?: string;
            contractEndDate?: string;
            note?: string;
        },
    ): Promise<void> {
        await portalApi.patch(API_ENDPOINTS.CONTRACT(orgId), payload);
    }

    async getGlobalSettings(): Promise<IGlobalCreditSettings> {
        const { data } = await portalApi.get<IGlobalCreditSettings>(API_ENDPOINTS.GLOBAL_SETTINGS);
        return data;
    }

    async updateGlobalSettings(payload: {
        warnThresholdPct?: number;
        dollarsPerCredit?: number;
        snowflakeToStudioRatio?: number;
        monthlyUnusedStrategy?: string;
        note?: string;
    }): Promise<IGlobalCreditSettings> {
        const { data } = await portalApi.patch<IGlobalCreditSettings>(
            API_ENDPOINTS.GLOBAL_SETTINGS,
            payload,
        );
        return data;
    }

    /** M4S-6013: the global AI ratio (null: AI credits are priced at the Snowflake ratio). */
    async getAiCreditRatio(): Promise<{ migrated: boolean; globalRatio: number | null }> {
        const { data } = await portalApi.get<{ migrated: boolean; globalRatio: number | null }>(
            API_ENDPOINTS.AI_RATIO,
        );
        return data;
    }

    async setAiCreditRatio(payload: {
        ratio: number | null;
        note?: string;
    }): Promise<{ migrated: boolean; globalRatio: number | null }> {
        const { data } = await portalApi.put<{ migrated: boolean; globalRatio: number | null }>(
            API_ENDPOINTS.AI_RATIO,
            payload,
        );
        return data;
    }

    async syncSalesforce(
        orgId: string,
        options?: { provisionAccess?: boolean },
    ): Promise<ISyncSalesforceResult> {
        const { data } = await portalApi.post<ISyncSalesforceResult>(
            API_ENDPOINTS.SYNC_SALESFORCE(orgId),
            options ?? {},
        );
        return data;
    }

    /** Read-only: preview what a Salesforce sync would provision (no DB write). */
    async previewSalesforce(orgId: string): Promise<SalesforceCreditPreview> {
        const { data } = await portalApi.get<SalesforceCreditPreview>(
            API_ENDPOINTS.PREVIEW_SALESFORCE(orgId),
        );
        return data;
    }

    async getRateCard(): Promise<ICreditRateCurrent[]> {
        const { data } = await portalApi.get<ICreditRateCurrent[]>(API_ENDPOINTS.RATE_CARD);
        return data;
    }

    async getRates(): Promise<ICreditRateCurrent[]> {
        return this.getRateCard();
    }

    async getPricingHistory(scope: 'global' | 'org' = 'global', orgId?: string) {
        const { data } = await portalApi.get(API_ENDPOINTS.PRICING_HISTORY, {
            params: { scope, orgId },
        });
        return data;
    }

    async getRateVersions(functionKey: string): Promise<ICreditRateVersion[]> {
        const { data } = await portalApi.get<ICreditRateVersion[]>(
            API_ENDPOINTS.RATE_VERSIONS(functionKey),
        );
        return data;
    }

    async publishRate(payload: IPublishCreditRatePayload): Promise<ICreditRateVersion> {
        const { data } = await portalApi.post<ICreditRateVersion>(API_ENDPOINTS.RATES, payload);
        return data;
    }

    async getMargins(orgId?: number): Promise<IMarginReport> {
        const { data } = await portalApi.get<IMarginReport>(API_ENDPOINTS.MARGINS, {
            params: orgId != null ? { orgId } : undefined,
        });
        return data;
    }

    async getCogs(start: string, end: string): Promise<ICogsResponse> {
        const session = await getSession();
        const { data } = await portalApi.get<ICogsResponse>(API_ENDPOINTS.COGS, {
            params: { start, end },
            headers: session?.accessToken
                ? { 'X-Cognito-Access-Token': session.accessToken }
                : undefined,
        });
        return data;
    }

    /** The shadow report (plan step 18): one report day, the newest unless `date` is given. */
    async getLedgerShadow(date?: string): Promise<ILedgerShadowReport> {
        const { data } = await portalApi.get<ILedgerShadowReport>(API_ENDPOINTS.LEDGER_SHADOW, {
            params: date ? { date } : undefined,
        });
        return data;
    }

    async addLedgerShadowNote(payload: IAddLedgerShadowNotePayload): Promise<ILedgerShadowNote> {
        const { data } = await portalApi.post<ILedgerShadowNote>(
            API_ENDPOINTS.LEDGER_SHADOW_NOTES,
            payload,
        );
        return data;
    }

    async recordCogsInvoice(payload: ICogsInvoiceInput): Promise<void> {
        await portalApi.post(API_ENDPOINTS.COGS_INVOICES, payload);
    }

    async getCosts(): Promise<ICreditCostCurrent[]> {
        const { data } = await portalApi.get<ICreditCostCurrent[]>(API_ENDPOINTS.COSTS);
        return data;
    }

    async publishCost(payload: IPublishCreditCostPayload): Promise<ICreditCostVersion> {
        const { data } = await portalApi.post<ICreditCostVersion>(API_ENDPOINTS.COSTS, payload);
        return data;
    }

    async searchOrgs(
        searchTerm: string,
        limit = 20,
        options?: { includeBalance?: boolean; nearOrgId?: number },
    ): Promise<IOrgSearchResult[]> {
        const { data } = await portalApi.get<IOrgSearchResult[]>(API_ENDPOINTS.ORG_SEARCH, {
            params: {
                searchTerm,
                limit,
                ...(options?.includeBalance ? { includeBalance: 1 } : {}),
                ...(options?.nearOrgId ? { nearOrgId: options.nearOrgId } : {}),
            },
        });
        return data;
    }

    // -----------------------------------------------------------------------------------------
    // CS actions (M4S-5911, docs/pdlc/credits-ui.md §5, plan step 23). Staff only.
    // -----------------------------------------------------------------------------------------

    /** One org's balance from the one read function (§2.4); `reader` gates the CS actions. */
    async getOrgBalance(orgId: string): Promise<CreditBalance> {
        const { data } = await portalApi.get<CreditBalance>(API_ENDPOINTS.ORG_BALANCE(orgId));
        return data;
    }

    async getAdjustmentPermissions(): Promise<AdjustmentPermissions> {
        const { data } = await portalApi.get<AdjustmentPermissions>(
            API_ENDPOINTS.ADJUSTMENT_PERMISSIONS,
        );
        return data;
    }

    async getAdjustmentReasons(): Promise<AdjustmentReason[]> {
        const { data } = await portalApi.get<AdjustmentReason[]>(API_ENDPOINTS.ADJUSTMENT_REASONS);
        return data;
    }

    /** The CS rows for one org, each with its `reverse` rights for this staff member. */
    async getOrgAdjustments(orgId: string, limit = 100): Promise<AdjustmentHistoryRow[]> {
        const { data } = await portalApi.get<AdjustmentHistoryRow[]>(API_ENDPOINTS.ADJUSTMENTS, {
            params: { orgId, limit },
        });
        return data;
    }

    async addCredits(orgId: string, payload: AddCreditsPayload): Promise<AdjustmentResult> {
        const { data } = await portalApi.post<AdjustmentResult>(
            API_ENDPOINTS.ADD_CREDITS(orgId),
            payload,
        );
        return data;
    }

    async removeCredits(orgId: string, payload: RemoveCreditsPayload): Promise<AdjustmentResult> {
        const { data } = await portalApi.post<AdjustmentResult>(
            API_ENDPOINTS.REMOVE_CREDITS(orgId),
            payload,
        );
        return data;
    }

    async transferCredits(payload: TransferCreditsPayload): Promise<AdjustmentResult> {
        const { data } = await portalApi.post<AdjustmentResult>(API_ENDPOINTS.TRANSFERS, payload);
        return data;
    }

    async reverseRow(rowId: number, payload: ReverseRowPayload): Promise<AdjustmentResult> {
        const { data } = await portalApi.post<AdjustmentResult>(
            API_ENDPOINTS.REVERSE_ROW(rowId),
            payload,
        );
        return data;
    }

    async extendGrant(
        orgId: string,
        rowId: number,
        payload: ExtendGrantPayload,
    ): Promise<AdjustmentResult> {
        const { data } = await portalApi.post<AdjustmentResult>(
            API_ENDPOINTS.EXTEND_GRANT(orgId, rowId),
            payload,
        );
        return data;
    }
}

export const creditConsumptionService = new CreditConsumptionService();
